import React, { useEffect, useState } from 'react';
import LoadingBar from 'react-top-loading-bar';
import { useTransition, animated } from 'react-spring';
import '../App.css';
import { FaArrowCircleUp } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

import NavbarBlog from '../components/navbarBlog.tsx';
import Footer from '../components/footer.tsx';

import usePreventZoom from '../preventZoom.js';

import Header from './blog/header.tsx';
import BlogView from './blog/blogView.tsx';

import { useBlogData } from './blogModule/useBlogData.ts';

// Import SAFMC images
import BG_SAFMC from '../assets/Blog/SAFMC/BG_SAFMC.webp';
import BG_SAFMC_PART1 from "../assets/Blog/SAFMC/SAFMC_PART1/HEADER_SAFMC_PART1.webp";
import BG_SAFMC_PART2 from "../assets/Blog/SAFMC/SAFMC_PART2/HEADER_SAFMC_PART2.webp";
import BG_SAFMC_PART3 from "../assets/Blog/SAFMC/SAFMC_PART3/HEADER_SAFMC_PART3.webp";
import BG_SAFMC_PART4 from "../assets/Blog/SAFMC/SAFMC_PART4/HEADER_SAFMC_PART4.webp";
import BG_SAFMC_PART5 from "../assets/Blog/SAFMC/SAFMC_PART5/HEADER_SAFMC_PART5.webp";
import BG_SAFMC_PART6 from "../assets/Blog/SAFMC/SAFMC_PART6/HEADER_SAFMC_PART6.webp";
import BG_SAFMC_PART7 from "../assets/Blog/SAFMC/SAFMC_PART7/HEADER_SAFMC_PART7.webp";

import BG_ACL from "../assets/Blog/ACL/BG_ACL.webp";
import BG_ACL_PART1 from "../assets/Blog/ACL/ACL_PART1/HEADER_ACL_PART1.webp";
import BG_ACL_PART2 from "../assets/Blog/ACL/ACL_PART2/HEADER_ACL_PART2.webp";
import BG_ACL_PART3 from "../assets/Blog/ACL/ACL_PART3/HEADER_ACL_PART3.webp";
import BG_ACL_PART4 from "../assets/Blog/ACL/ACL_PART4/HEADER_ACL_PART4.webp";
import BG_ACL_PART5 from "../assets/Blog/ACL/ACL_PART5/HEADER_ACL_PART5.webp";
import BG_ACL_PART6 from "../assets/Blog/ACL/ACL_PART6/HEADER_ACL_PART6.webp";
import BG_ACL_PART7 from "../assets/Blog/ACL/ACL_PART7/HEADER_ACL_PART7.webp";
import BG_ACL_PART8 from "../assets/Blog/ACL/ACL_PART8/HEADER_ACL_PART8.webp";
import BG_ACL_PART9 from "../assets/Blog/ACL/ACL_PART9/HEADER_ACL_PART9.webp";


export default function Blog() {
    const location = useLocation();
    const [isMd, setIsMd] = useState(window.matchMedia('(min-width: 900px)').matches);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [progress, setProgress] = useState(0);

    // Determine current series from URL
    const currentSeries = location.pathname.includes('/acl') ? 'acl' : 'safmc';
    
    // Get data for current series
    const { blogSeries } = useBlogData(currentSeries);
    const series = blogSeries[0]; // Get first (and only) series

    useEffect(() => {
        setProgress(30);
        setTimeout(() => {
            setProgress(100);
        }, 600);
    }, [currentSeries]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentSeries]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 900px)');
        const updateIsMd = () => {
            setIsMd(mediaQuery.matches);
        };

        mediaQuery.addListener(updateIsMd);
        return () => {
            mediaQuery.removeListener(updateIsMd);
        };
    }, []);

    const handleScroll = () => {
        if (window.scrollY > 350) {
            setShowScrollToTop(true);
        } else {
            setShowScrollToTop(false);
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const arrowTransition = useTransition(showScrollToTop, {
        from: { opacity: 0, transform: 'translateY(100%)' },
        enter: { opacity: 1, transform: 'translateY(0%)' },
        leave: { opacity: 0, transform: 'translateY(100%)' },
        config: { tension: 300, friction: 20 },
    });

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: false });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    usePreventZoom();

    // Map images based on series
    const getBackgroundImage = () => {
        if (currentSeries === 'safmc') {
            return BG_SAFMC;
        }
        if (currentSeries === 'acl') {
            return BG_ACL;
        }
        return '';
    };

    const getPostImages = () => {
        if (currentSeries === 'safmc') {
            return [BG_SAFMC_PART1, BG_SAFMC_PART2, BG_SAFMC_PART3, BG_SAFMC_PART4, BG_SAFMC_PART5, BG_SAFMC_PART6, BG_SAFMC_PART7];
        }
        if (currentSeries === 'acl') {
            // Return placeholder images untuk ACL (atau ganti dengan real images nanti)
            return [BG_ACL_PART1, BG_ACL_PART2, BG_ACL_PART3, BG_ACL_PART4, BG_ACL_PART5, BG_ACL_PART6, BG_ACL_PART7, BG_ACL_PART8, BG_ACL_PART9
            ];
        }
        return [];
    };

    // Map posts with images
    const postsWithImages = series?.posts.map((post, index) => ({
    ...post,
    imageUrl: getPostImages()[index] || '',
    })) || [];

    if (!series) {
        return (
            <div className="App">
                <NavbarBlog />
                <div className='pt-20 min-w-screen text-center py-20'>
                    <p className="text-textGrey text-xl">Blog series not found</p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="App">
            <NavbarBlog />
            <div className='pt-20 min-w-screen'>
                {/* Loading Bar */}
                <LoadingBar color="#8C001f" progress={progress} onLoaderFinished={() => setProgress(0)} />
                <section className='hidden w-64 mr-10 group transform hover:translate-y-[-5px] transition-transform duration-300 ease-in-out'>
                    <script type='text/javascript' id='mapmyvisitors' src='https://mapmyvisitors.com/map.js?cl=e1e1e1&w=300&t=n&d=fngBtcVXvPP4eW7t-ugYnO9uc3XJHrNYu7CAB7PGnao&co=ffffff&cmo=8c001f&cmn=d4201e&ct=ffffff'></script>
                </section>
                
                {/* Header */}
                <span id={`${currentSeries}`}>
                    <Header 
                        backgroundImage={getBackgroundImage()} 
                        title={series.title}
                        backgroundPosition={series.backgroundPosition}
                    />
                </span>

                {/* YouTube Section - Only for SAFMC */}
                {series.youtubeEmbedId && (
                    <>
                        <div id={`${currentSeries}-after-movie`} className='font-condensed text-textGrey md:text-4xl text-3xl font-bold mb-2 mt-8'>
                            AFTER MOVIE
                            {series.youtubeChannel && (
                                <p className='text-lg font-extralight'>
                                    Recorded by: <a href={series.youtubeChannelUrl || '#'} className='text-textRed'>{series.youtubeChannel}</a>
                                </p>
                            )}
                        </div>
                        <div className='w-full flex sm:flex-col lg:flex-row flex-col justify-center items-center mb-16'>
                            <div className='afterMovie-container w-[22rem] sm:w-[44rem] md:w-[48rem] lg:w-[54rem] flex md:flex md:flex-row flex-col justify-center items-center'>
                                <div className="embedded-container aspect-w-16 aspect-h-9 w-11/12">
                                    <iframe
                                        className="video-iframe w-full h-full rounded-xl"
                                        src={`https://www.youtube.com/embed/${series.youtubeEmbedId}`}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Story Telling Section */}
                <div id={`${currentSeries}-story-telling`} className='font-condensed text-textGrey md:text-4xl text-3xl font-bold mt-8'>
                    STORYTELLING BLOG
                </div>
                <div className='my-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-2 gap-y-4 justify-items-center'>
                    {postsWithImages.map((post) => (
                        <Link key={post.postId} to={post.route}>
                            <BlogView {...post} />
                        </Link>
                    ))}
                </div>
            </div>
            
            <Footer />
            {arrowTransition((style, item) =>
                item ? (
                    <animated.div
                        style={style}
                        className="fixed bottom-5 right-5 cursor-pointer z-10"
                        onClick={scrollToTop}
                    >
                        <FaArrowCircleUp
                            size={isMd ? 32 : 24}
                            className="text-[#c0c5ca] hover:scale-110"
                        />
                    </animated.div>
                ) : null
            )}
        </div>
    );
}
